import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/ui-kit";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Stethoscope, Building2 } from "lucide-react";

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const buildIcon = (color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color}; width:24px; height:24px; border-radius:50% 50% 50% 0; transform:rotate(-45deg); border:2px solid white; box-shadow:0 2px 4px rgba(0,0,0,0.3); display:grid; place-items:center;"><div style="transform:rotate(45deg); color:white; font-weight:bold; font-size:11px;"></div></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});

const docIcon = buildIcon("#7c3aed");
const phIcon = buildIcon("#1d4ed8");
const phApIcon = buildIcon("#10b981");

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [points, map]);
  return null;
}

export default function MapView() {
  const { t } = useLang();
  const [doctors, setDoctors] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [filter, setFilter] = useState({ doctors: true, pharmacies: true, governorate: "" });

  useEffect(() => { (async () => {
    const [d, p] = await Promise.all([api.get("/doctors"), api.get("/pharmacies")]);
    setDoctors(d.data); setPharmacies(p.data);
  })(); }, []);

  const filterFn = (item) => {
    if (filter.governorate && item.governorate !== filter.governorate) return false;
    return item.latitude && item.longitude;
  };
  const visibleDocs = filter.doctors ? doctors.filter(filterFn) : [];
  const visiblePhs = filter.pharmacies ? pharmacies.filter(filterFn) : [];
  const allPoints = [...visibleDocs, ...visiblePhs];

  const center = allPoints.length > 0
    ? [allPoints[0].latitude, allPoints[0].longitude]
    : [30.7865, 31.0004]; // Tanta default

  return (
    <div data-testid="map-page">
      <PageHeader title="Map View" subtitle="Geographic visualization of doctors & pharmacies"/>

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filter.doctors} onChange={(e) => setFilter({...filter, doctors: e.target.checked})}/>
            <Stethoscope className="w-4 h-4 text-purple-600"/>
            Doctors ({doctors.filter(filterFn).length})
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filter.pharmacies} onChange={(e) => setFilter({...filter, pharmacies: e.target.checked})}/>
            <Building2 className="w-4 h-4 text-blue-600"/>
            Pharmacies ({pharmacies.filter(filterFn).length})
          </label>
          <select value={filter.governorate} onChange={(e) => setFilter({...filter, governorate: e.target.value})} className="text-sm rounded ring-1 ring-slate-300 px-2 py-1">
            <option value="">All Governorates</option>
            <option>Gharbia</option><option>Kafr El Sheikh</option>
          </select>
          <div className="ms-auto text-xs text-slate-500">{allPoints.length} on map · No GPS = not shown</div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div style={{ height: "70vh", minHeight: "500px" }}>
          {allPoints.length > 0 ? (
            <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={allPoints}/>
              {visibleDocs.map(d => (
                <Marker key={`d-${d.id}`} position={[d.latitude, d.longitude]} icon={docIcon}>
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-bold">{d.full_name}</div>
                      <div className="text-xs text-slate-600">{d.specialty}</div>
                      <div className="text-xs"><Badge color="purple">{d.kol_classification}</Badge></div>
                      <div className="text-xs text-slate-500">{d.city} · {d.area}</div>
                      <div className="text-xs font-mono">{d.mobile}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {visiblePhs.map(p => (
                <Marker key={`p-${p.id}`} position={[p.latitude, p.longitude]} icon={p.classification === "A+" ? phApIcon : phIcon}>
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-bold">{p.name}</div>
                      <div className="text-xs"><Badge color={p.classification === "A+" ? "green" : "blue"}>{p.classification}</Badge></div>
                      <div className="text-xs text-slate-600">{p.owner_name}</div>
                      <div className="text-xs text-slate-500">{p.city} · {p.area}</div>
                      <div className="text-xs font-mono">{p.mobile}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          ) : (
            <div className="h-full grid place-items-center text-slate-400">
              <div className="text-center">
                <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
                <p>No GPS coordinates yet. Add latitude/longitude to doctors and pharmacies to see them on the map.</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
